import { Component, Input, OnInit } from '@angular/core';
import { Upload, UploadBatchValidationStatus } from '@models/ui/upload.model';
import { appear } from '../../animations/appear';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { UploadBatchState } from '@models/ui/case-analytics-state/upload-batch-state.model';
import { Router } from "@angular/router"
import { CaseAnalyticsItemBaseComponent } from '../case-analytics-item-base.component';
import { ThemeService as ChartThemeService } from 'ng2-charts';
import { LexbeThemeService as LexbeThemeService } from '@services/lexbe-theme.service';
import { Chart, LegendItem } from 'chart.js';
import { Legend } from 'chart.js/dist/plugins/plugin.legend';

@Component({
    selector: 'lex-case-analytics-uploads',
    templateUrl: './case-analytics-uploads.component.html',
    styleUrls: ['./case-analytics-uploads.component.scss'],
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsUploadsComponent extends CaseAnalyticsItemBaseComponent implements OnInit {
  @Input() set uploadBatchState(state: UploadBatchState) {
    this.uploadBatchState$.next(state);
  }

  /*TODO: It looks like quite a few of these options will be shared amongst the graphs. Consider placing chart options in a factory*/
  public ngChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: {
      xAxisKey: 'id',
      yAxisKey: 'nested.docCount'
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          generateLabels: (chart: Chart) => {
            const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
            const i = labels.findLastIndex(label => label.text === this.highlightedLegendItem?.text);

            if (i != -1) {
              labels[i].fillStyle = this.highlightedLegendItem.fillStyle;
              labels[i].fontColor = this.highlightedLegendItem.fontColor;
            }

            return labels;
          },
          filter: (legendItem, chartData) => {
            const i = chartData.datasets.findLastIndex(bar => bar.label === legendItem.text);
            if (legendItem.datasetIndex != i) {
              return false;
            }
            return true;
          },
        },
        onHover: (event, legendItem: LegendItem, legend: Legend) => {
          event.native.target.classList.remove('pointerOnHover');
          event.native.target.classList.add('onLegendItem');

          this.highlightedLegendItem = legendItem;

          const activeDatasets = [];
          for (let i = 0; i < this.chartData.datasets.length; i++) {
            if (legend.chart.getDatasetMeta(i).label === legendItem.text) {
              activeDatasets.push({ datasetIndex: i, index: 0 });
              if (activeDatasets.length === 1) {
                const iconHoverColor = legend.chart.data.datasets[i].hoverBackgroundColor.toString();
                this.highlightedLegendItem.fillStyle = iconHoverColor;
                this.highlightedLegendItem.fontColor = this.chartFontCurrentColors.hover;
              }
            }
          }
          legend.chart.setActiveElements(
            activeDatasets,
          );
          legend.chart.update();

        },
        onLeave: (event, legendItem: LegendItem, legend: Legend) => {
          event.native.target.classList.remove('pointerOnHover');
          event.native.target.classList.remove('onLegendItem');


          this.highlightedLegendItem = legendItem;
          const fillStyle = legend.chart.data.datasets[legendItem.datasetIndex].backgroundColor.toString();
          this.highlightedLegendItem.fillStyle = fillStyle;
          this.highlightedLegendItem.fontColor = this.chartFontCurrentColors.normal;

          legend.chart.setActiveElements([]);
          legend.chart.update();
        },
        onClick: null,
      },
      tooltip: {
        enabled: false,
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          display: false
        },
        stacked: true,
      },
      y: {
        beginAtZero: true,
        stacked: true,
      },
    },
  }

  public ngChartPlugins = [
    {
      id: 'hideUploadBatchSummary',
      beforeEvent: (chart, args) => {
        const event = args.event;
        if ((args.inChartArea === false || event.type === 'mouseout')) {
          event.native.target.classList.remove('pointerOnHover')
          if (!event.native.target.classList.contains('onLegendItem')) {
            this.uploadBatchSummary = null;
            this.highlightedLegendItem = null;
            chart.setActiveElements([]);
            chart.update();
          }
        }
    }},
    {
    id: 'chartIsReady',
    afterRender: (chart: Chart) => {
      if (this.chartData.datasets.length > 0) {
        this.labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
        this.shouldShowLoader = false;
      }
    }
  }];

  public chartData = {
    datasets: []
  };

  public labels: LegendItem[];
  public shouldShowLoader: boolean = true;

  public caseUploadBatchCount: number = 0;
  public caseDocCount: number = 0;
  public displayedBatchCount: number = 0;

  public uploadBatchValidationStatus = UploadBatchValidationStatus;

  public uploadBatchSummary: UplaodBatchSummary = null;

  private customColorsScheme = new Map<UploadBatchValidationStatus, number[]>([
    [UploadBatchValidationStatus.NotRun, [110, 119, 144]],
    [UploadBatchValidationStatus.Passed, [42, 167, 143 ]],
    [UploadBatchValidationStatus.Failed, [215, 38, 56 ]],
    [UploadBatchValidationStatus.Error,  [215, 38, 56 ]],
    [UploadBatchValidationStatus.Warnings, [255, 193, 7 ]],
    [UploadBatchValidationStatus.Info, [36, 62, 144 ]],
    [UploadBatchValidationStatus.Unsupported, [136, 146, 183 ]],
    [UploadBatchValidationStatus.Validating, [171, 176, 230 ]]
  ]);

  private uploadBatchState$ = new BehaviorSubject<UploadBatchState>(null);

  constructor(private router: Router,
    lexbeThemeService: LexbeThemeService,
    chartThemeService: ChartThemeService) {
    super(lexbeThemeService, chartThemeService);
  }

  ngOnInit(): void {
    this.uploadBatchState$.pipe(
      filter((response) => (!!response)),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      if(response.uploadBatches.length < 1) {
        return null;
      }
      this.caseUploadBatchCount = response.caseUploadBatchCount;
      this.caseDocCount = response.totalDocCount;
      this.displayedBatchCount = response.uploadBatches.length;

      const datasets = [];
      response.uploadBatches.sort((a: Upload, b: Upload) => b.uploadDate < a.uploadDate ? 1 : -1);
      response.uploadBatches.forEach(uploadBatch => {
        const bar = this.generateBarData(uploadBatch);
        datasets.push(bar);
      });

      this.chartData.datasets = datasets;
    });
  }
  public generateBarData(uploadBatch: Upload) {
    const baseColor = this.getBaseBarColor(uploadBatch.validationStatus);
    const hoverColor = this.getHoverBarColor(uploadBatch.validationStatus);
    const displayedValidationStatus = uploadBatch.validationStatus === "NotRun" ? "Needs Validation" : uploadBatch.validationStatus;

    const barData = {
      id: uploadBatch.id,
      nested: {
        docCount: uploadBatch.docCount,
        batchTitle: uploadBatch.title,
        validationStatus: uploadBatch.validationStatus,
        validationReportRoute: uploadBatch.validationReportRoute
      }
    };

    return {
      label: displayedValidationStatus,
      data: [barData],
      backgroundColor: baseColor,
      borderColor: baseColor,
      hoverBackgroundColor: hoverColor,
      barThickness: 24,
      minBarLength: 5,
    }
  }

  public getBaseBarColor(validationStatus: UploadBatchValidationStatus): string {
    const rgb = this.customColorsScheme.get(validationStatus);
    return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  }

  public getHoverBarColor(validationStatus: UploadBatchValidationStatus): string {
    const rgb = this.customColorsScheme.get(validationStatus);
    const hoverBrightness = 40;
    const hoverRgb = rgb.map(value => value + hoverBrightness <= 255 ? value + hoverBrightness : 255);
    return `rgb(${hoverRgb[0]}, ${hoverRgb[1]}, ${hoverRgb[2]})`;
  }

  public displayBatchSummary(chartEvent) {
    if (chartEvent.active.length === 1) {
      chartEvent.event.native.target.classList.add('pointerOnHover')

      const i = chartEvent.active[0].datasetIndex;
      const data = this.chartData.datasets[i].data[0].nested;
      this.uploadBatchSummary = {
        batchTitle: data.batchTitle,
        docCount: data.docCount,
        validationStatus: data.validationStatus
      }

      const label = this.labels.findLast(label => label.text === this.chartData.datasets[i].label);
      label.fillStyle = this.chartData.datasets[i].hoverBackgroundColor.toString();
      label.fontColor = this.chartFontCurrentColors.hover;

      this.highlightedLegendItem = label;
    }
    else {
      chartEvent.event.native.target.classList.remove('pointerOnHover')

      this.uploadBatchSummary = null;
      this.highlightedLegendItem = null;
    }

    chartEvent.event.chart.update();
  }

  public selectBatch(chartEvent) {
    if (chartEvent.active.length != 1) return;
    const i = chartEvent.active[0].datasetIndex;
    const data = this.chartData.datasets[i].data[0].nested;
    if (data.validationStatus === UploadBatchValidationStatus.NotRun) {
      this.router.navigate(['/', 'case', 'uploads']);
    }
    else {
      this.router.navigateByUrl(data.validationReportRoute);
    }
  }
}

interface UplaodBatchSummary {
  batchTitle: string,
  docCount: number,
  validationStatus: UploadBatchValidationStatus
}
